import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { Auth0Provider } from "@auth0/auth0-react";
import history from "./utils/history";
import { getConfig } from "./config";

const onRedirectCallback = (appState) => {
  history.push(
    appState && appState.returnTo ? appState.returnTo : window.location.pathname
  );
};

const host = window.location.hostname;
const callBack = host === 'localhost' ? 'http://localhost:3000' : 'https://ninjaskiptracing.com/';

// ONE-OFF CACHE PURGE FOR THE CUSTOM-DOMAIN CUTOVER.
//
// The SDK caches tokens in localStorage under "@@auth0spajs@@::<clientId>::<audience>::<scope>".
// The ISSUING DOMAIN IS NOT PART OF THAT KEY, and neither the clientId nor the audience
// changed in this migration - so after the switch the SDK would find a cached token minted by
// the old dev tenant, consider it valid, and hand it to an API that now only accepts
// iss=https://auth.ninjaskiptracing.com/. Every request 401s until the token happens to
// expire, and getAccessTokenSilently keeps returning the stale one meanwhile.
//
// auth0-spa-js 2.0.8 has no cacheKeyPrefix option (the prefix is a hardcoded constant), so
// the cache is cleared directly, once, guarded by a marker key. Users log in again once.
// This block can be deleted after everyone has cycled through - it is idempotent and cheap
// until then.
// auth0-spa-js 2.0.8 keeps state in FOUR places, and all four have to go. Clearing only the
// localStorage token cache produces:
//   "Issuer (iss) claim mismatch in the ID token; expected https://auth.ninjaskiptracing.com/,
//    found https://dev-t8ituz8ye8noyowf.us.auth0.com/"
// because a login STARTED before the cutover leaves a pending transaction in sessionStorage.
// The new bundle then completes that old transaction and receives an ID token stamped with
// the old issuer, which it correctly refuses.
const CUTOVER_MARKER = "auth0_issuer_cutover_v2";
if (!localStorage.getItem(CUTOVER_MARKER)) {
  // 1. cached access/ID tokens
  Object.keys(localStorage)
    .filter((k) => k.startsWith("@@auth0spajs@@"))
    .forEach((k) => localStorage.removeItem(k));
  // 2. in-flight login transaction - the one that caused the iss mismatch
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith("a0.spajs.txs.") || k.startsWith("@@auth0spajs@@"))
    .forEach((k) => sessionStorage.removeItem(k));
  // 3. the SSO hint cookie and its legacy twin, which make the SDK attempt a silent auth
  //    against a session that no longer matches the configured domain
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (!name) return;
    if (name === "auth0.is.authenticated" || name === "_legacy_auth0.is.authenticated" ||
        name.startsWith("a0.spajs.txs.") || name.startsWith("_legacy_a0.spajs.txs.")) {
      document.cookie = `${name}=; Max-Age=0; path=/`;
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
    }
  });
  localStorage.setItem(CUTOVER_MARKER, String(Date.now()));
}

// Please see https://auth0.github.io/auth0-react/interfaces/Auth0ProviderOptions.html
// for a full list of the available properties on the provider
const config = getConfig();

const providerConfig = {
  domain: config.domain,
  clientId: config.clientId,
  onRedirectCallback,
  cacheLocation:"localstorage",
  useRefreshToken:true,
  scope:"read:current_user",
  authorizationParams: {
    redirect_uri: callBack,
    ...(config.audience ? { audience: config.audience } : null),
  },
};

const root = createRoot(document.getElementById('root'));
root.render(
  <Auth0Provider scope="read:current_user"
    {...providerConfig}
  >
    <App />
  </Auth0Provider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
